import { useAuthenticatedUser } from "@src/components/user/user-context";
import { NavBarItemType, NavbarSection } from "@src/gql/graphql";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
	NAVBAR_BRANCH_QUERY_KEY_ROOT,
	type NavbarBranchItem,
	type SharedWithMeListAccess,
	getSharedWithMeListItemAccess,
	navbarBranchIsSharedOrPublished,
	navbarBranchQueryKeys,
	useNavbarTreePrefetch,
} from "./fetch-ranklist";

export interface FlatTreeRow {
	key: string;
	name: string;
	type: NavBarItemType;
	depth: number;
	parentKey: string | null;
	/** My Boards / Favourites: collaborators or published link. */
	isSharedOrPublished: boolean;
	/** Shared With Me boards only; eye vs pencil in UI. */
	sharedWithMeListItemAccess?: SharedWithMeListAccess;
	rank: string;
}

function toFlatRow(
	item: NavbarBranchItem,
	depth: number,
	parentKey: string | null,
	section: NavbarSection,
	currentUserId: string,
	currentUserEmail?: string,
): FlatTreeRow {
	const base = {
		key: item.key,
		name: item.name,
		type: item.type,
		depth,
		parentKey,
		rank: item.rank,
	};
	if (
		section === NavbarSection.SharedWithMe &&
		item.type !== NavBarItemType.Folder
	) {
		return {
			...base,
			isSharedOrPublished: false,
			sharedWithMeListItemAccess: getSharedWithMeListItemAccess(
				item,
				currentUserId,
				currentUserEmail,
			),
		};
	}
	return {
		...base,
		isSharedOrPublished: navbarBranchIsSharedOrPublished(item),
	};
}

/**
 * Walks the per-branch React Query caches (the same ones the navbar reads,
 * which mutations keep up to date optimistically) and returns a depth-first
 * flat list for modal-style rendering. This is the single source of truth:
 * any optimistic update a mutation performs on the branch caches shows up
 * here immediately.
 */
export function buildFlatTreeFromBranchCache(
	queryClient: QueryClient,
	section: NavbarSection,
	currentUserId: string,
	currentUserEmail?: string,
): FlatTreeRow[] {
	const rows: FlatTreeRow[] = [];
	const sortByRank = (a: NavbarBranchItem, b: NavbarBranchItem) =>
		a.rank.localeCompare(b.rank, undefined, { numeric: true });

	const readBranch = (parentKey: string | null): NavbarBranchItem[] => {
		const data = queryClient.getQueryData<NavbarBranchItem[]>(
			navbarBranchQueryKeys.branch(section, parentKey),
		);
		return data ? [...data].sort(sortByRank) : [];
	};

	const traverse = (parentKey: string | null, depth: number) => {
		for (const item of readBranch(parentKey)) {
			rows.push(
				toFlatRow(
					item,
					depth,
					parentKey,
					section,
					currentUserId,
					currentUserEmail,
				),
			);
			if (item.type === NavBarItemType.Folder) {
				traverse(item.key, depth + 1);
			}
		}
	};

	traverse(null, 0);
	return rows;
}

const EMPTY_FLAT_TREE_ROWS: FlatTreeRow[] = [];
const getEmptyServerSnapshot = () => "";

export interface UseFlatNavbarTreeOptions {
	enabled?: boolean;
}

/**
 * React hook that returns a depth-first flat list of the whole navbar tree
 * for a section. Subscribes to changes in any per-branch cache (and the
 * tree-prefetch query) so optimistic mutations update the UI immediately.
 */
export function useFlatNavbarTree(
	section: NavbarSection,
	options?: UseFlatNavbarTreeOptions,
): FlatTreeRow[] {
	const enabled = options?.enabled ?? true;
	const queryClient = useQueryClient();
	const { id: currentUserId, email: currentUserEmail } = useAuthenticatedUser();
	const prefetchOptions = useMemo(() => ({ enabled }), [enabled]);
	// Ensure the tree endpoint has been fetched at least once and its results
	// seeded into the per-branch caches, so readers have something to read.
	useNavbarTreePrefetch(section, prefetchOptions);

	const subscribe = useCallback(
		(notify: () => void) => {
			if (!enabled) {
				return () => {};
			}
			let cancelled = false;
			let microtaskScheduled = false;
			const unsub = queryClient.getQueryCache().subscribe((event) => {
				if (
					event.type !== "added" &&
					event.type !== "removed" &&
					event.type !== "updated"
				) {
					return;
				}
				const key = event.query.queryKey;
				if (
					Array.isArray(key) &&
					key[0] === NAVBAR_BRANCH_QUERY_KEY_ROOT &&
					key[1] === section
				) {
					// Defer and coalesce so cache updates from another component
					// mounting useQuery or batch-seeding branches do not notify here
					// synchronously or repeatedly within the same microtask turn.
					if (!microtaskScheduled) {
						microtaskScheduled = true;
						queueMicrotask(() => {
							microtaskScheduled = false;
							if (!cancelled) {
								notify();
							}
						});
					}
				}
			});
			return () => {
				cancelled = true;
				unsub();
			};
		},
		[enabled, queryClient, section],
	);

	const getSnapshot = useCallback(() => {
		if (!enabled) {
			return "";
		}
		// A cheap signature: concatenated updatedAt stamps of every branch query
		// for this section. Any branch update bumps the string, triggering a
		// re-read through the memo below.
		return queryClient
			.getQueryCache()
			.findAll({ queryKey: navbarBranchQueryKeys.section(section) })
			.map((q) => `${q.queryKey.join("/")}@${q.state.dataUpdatedAt}`)
			.join("|");
	}, [enabled, queryClient, section]);

	// Subscribe to any change under the navbar branch query prefix. This gives
	// us re-renders when mutations update any branch optimistically.
	const branchesSignature = useSyncExternalStore(
		subscribe,
		getSnapshot,
		getEmptyServerSnapshot,
	);

	return useMemo(
		() =>
			enabled && branchesSignature !== ""
				? buildFlatTreeFromBranchCache(
						queryClient,
						section,
						currentUserId,
						currentUserEmail,
					)
				: EMPTY_FLAT_TREE_ROWS,
		[
			enabled,
			queryClient,
			section,
			branchesSignature,
			currentUserId,
			currentUserEmail,
		],
	);
}
